/**
 * Category Select With Inline Create Component
 *
 * Reusable category multi-select with inline category creation (Pattern 1: Inline Dropdown Creation)
 *
 * Features:
 * - Multi-select with color bar indicators
 * - Custom tag rendering with color backgrounds
 * - Inline category creation via dropdownRender
 * - React Hook Form Controller compatible
 * - Full CLAUDE.md style compliance
 *
 * Usage:
 * <Controller
 *   name="categoryIds"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <CategorySelectWithCreate
 *       value={field.value}
 *       onChange={field.onChange}
 *       categories={categories}
 *       loading={loadingCategories}
 *       onCategoryCreated={handleCategoryCreated}
 *       error={fieldState.error?.message}
 *       placeholder={t('vendor.placeholders.categories')}
 *     />
 *   )}
 * />
 */

import { useState, useMemo } from 'react';
import { Select, Button, Divider, Tag, theme, Typography, App } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import { useFormStyles } from '@/styles/formStyles';
import { CATEGORY_COLOR_PRESETS } from '@/constants/categoryColorPresets';
import type { CategoryListItem } from '@/types/api/category';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import { useCategoryMutations } from '@/hooks/useCategories';

const { Text } = Typography;

export interface CategorySelectWithCreateProps {
  /** Selected category IDs */
  value?: number[];
  /** Change handler */
  onChange?: (value: number[]) => void;
  /** Available categories */
  categories: CategoryListItem[];
  /** Loading state */
  loading?: boolean;
  /** Callback when new category is created */
  onCategoryCreated?: () => void | Promise<void>;
  /** Error message */
  error?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
}

export const CategorySelectWithCreate: React.FC<CategorySelectWithCreateProps> = ({
  value = [],
  onChange,
  categories,
  loading = false,
  onCategoryCreated,
  error,
  placeholder = 'Select categories...',
  disabled = false,
}) => {
  const { token } = theme.useToken();
  const elevation = useElevation();
  const formStyles = useFormStyles();
  const { t } = useVendorTranslation();
  const { notification } = App.useApp();
  const { createCategory } = useCategoryMutations();

  const [searchValue, setSearchValue] = useState('');
  const [creating, setCreating] = useState(false);

  // Get random color from the centralized category preset palette
  const getRandomColor = () => {
    return CATEGORY_COLOR_PRESETS[Math.floor(Math.random() * CATEGORY_COLOR_PRESETS.length)];
  };

  // Sort categories by categoryId descending (newest first) and create options
  const categoryOptions = useMemo(() => {
    // Sort by categoryId descending so newly created categories appear at the top
    const sortedCategories = [...categories].sort((a, b) => b.categoryId - a.categoryId);
    return sortedCategories.map((cat) => ({
      label: (
        <div className="tw-flex tw-items-center tw-gap-2">
          <div
            style={{
              width: '3px',
              height: '16px',
              backgroundColor: cat.color || token.colorPrimary,
              borderRadius: radiusTokens.chip,
            }}
          />
          <span>{cat.category}</span>
        </div>
      ),
      value: cat.categoryId.toString(),
      searchLabel: cat.category, // For search filtering
    }));
  }, [categories, token.colorPrimary]);

  // Handle inline category creation - uses mutation hook for proper cache invalidation
  const handleInlineCreate = async () => {
    if (!searchValue.trim() || creating) return;

    const categoryName = searchValue.trim();

    try {
      setCreating(true);

      // Create category via mutation hook (invalidates cache before returning)
      // `isActive: true` used to ride here. It never reached the wire —
      // PartyCategoryCreateInput declares no such field — and a new category is
      // active by default on both seams, so the line asked for nothing and was
      // told it succeeded. Activation has its own root (categoryApi.toggleStatus
      // → partyCategorySetActive); it never travels on a create.
      const newCategory = await createCategory({
        category: categoryName,
        typeIds: [1], // Default to Vendor type
        description: '',
        color: getRandomColor(), // Random color from palette
      });

      // Refresh category list FIRST (so the new category is in the list)
      // Using local state pattern like wizards, setCategories updates synchronously
      if (onCategoryCreated) {
        await onCategoryCreated();
      }

      // Then auto-select the newly created category (list is already updated)
      if (newCategory?.categoryId) {
        const newValue = [...value, newCategory.categoryId];
        onChange?.(newValue);
      }

      setSearchValue('');
    } catch (err) {
      console.error('Failed to create category:', err);
      notification.error({
        message: t('vendor.notifications.systemError'),
        description: err instanceof Error ? err.message : t('vendor.messages.categoryCreateFailed'),
        placement: 'topRight',
        duration: 5,
      });
    } finally {
      setCreating(false);
    }
  };

  // Convert hex to rgba with opacity
  const hexToRgba = (hex: string, alpha: number = 0.1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <>
      <style>
        {`
          /* Category multi-select styling */
          .category-select-with-create .ant-select-selector {
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            border-radius: ${radiusTokens.card}px !important;
            padding: 4px 8px !important;
            display: flex !important;
            align-items: center !important;
          }

          /* Center placeholder text vertically */
          .category-select-with-create .ant-select-selection-placeholder {
            position: absolute !important;
            top: 0 !important;
            bottom: 0 !important;
            inset-inline-start: 11px !important;
            inset-inline-end: 11px !important;
            transform: none !important;
            display: flex !important;
            align-items: center !important;
            line-height: normal !important;
          }

          /* +N overflow tag styling */
          .category-select-with-create .ant-select-selection-overflow-item-suffix .ant-select-selection-item {
            background-color: ${token.colorFillQuaternary} !important;
            color: ${token.colorTextSecondary} !important;
            border: none !important;
          }

          /* Category dropdown styling — shadow-border elevation (useElevation().popover) */
          .category-dropdown-with-create {
            background-color: ${token.colorBgElevated} !important;
            border: ${elevation.popover.border} !important;
            border-radius: ${elevation.popover.borderRadius}px !important;
            box-shadow: ${elevation.popover.boxShadow} !important;
          }

          .category-dropdown-with-create .ant-select-item {
            padding: 8px 12px !important;
            min-height: ${controlHeightTokens.controlHeightXL}px !important;
            display: flex !important;
            align-items: center !important;
          }

          .category-dropdown-with-create .ant-select-item-option-content {
            display: flex !important;
            align-items: center !important;
          }

          /* Create button styling */
          .category-create-button {
            width: 100% !important;
            text-align: center !important;
            color: ${token.colorPrimary} !important;
            height: 40px !important;
            padding: 8px 12px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }

          .category-create-button:hover {
            background-color: ${token.colorPrimaryBg} !important;
          }
        `}
      </style>
      <Select
        mode="multiple"
        size="large"
        placeholder={placeholder}
        showSearch
        loading={loading}
        disabled={disabled}
        maxTagCount="responsive"
        value={value.map((v) => v.toString())}
        onChange={(stringValues) => {
          // Convert string values to numbers
          const numericValues = Array.isArray(stringValues)
            ? stringValues.map((v) => (typeof v === 'string' ? parseInt(v) : v))
            : [];
          onChange?.(numericValues);
        }}
        onSearch={setSearchValue}
        searchValue={searchValue}
        onSelect={() => {
          // Clear search input after selecting an option
          setSearchValue('');
        }}
        filterOption={(input, option) => {
          const searchLabel = (option as { searchLabel?: string })?.searchLabel ?? '';
          return searchLabel.toLowerCase().includes(input.toLowerCase());
        }}
        options={categoryOptions}
        tagRender={(props) => {
          const category = categories.find((cat) => cat.categoryId.toString() === props.value);
          // Skip rendering if category not found (filtered out)
          if (!category) return <></>;

          const categoryColor = category.color || token.colorPrimary;

          return (
            <Tag
              closable={props.closable}
              onClose={props.onClose}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                margin: '2px 4px 2px 0',
                padding: '2px 8px',
                borderRadius: radiusTokens.control,
                backgroundColor: hexToRgba(categoryColor, 0.1),
                color: categoryColor,
                border: 'none',
              }}
            >
              <div
                style={{
                  width: '3px',
                  height: '12px',
                  backgroundColor: categoryColor,
                  borderRadius: radiusTokens.chip,
                }}
              />
              {category.category}
            </Tag>
          );
        }}
        popupRender={(menu) => (
          <>
            {menu}
            {searchValue &&
              !categories.some((c) => c.category.toLowerCase() === searchValue.toLowerCase()) && (
                <>
                  <Divider style={{ margin: '8px 0' }} />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={handleInlineCreate}
                    loading={creating}
                    className="category-create-button"
                  >
                    {t('vendor.buttons.createCategoryWithName', { name: searchValue })}
                  </Button>
                </>
              )}
          </>
        )}
        className="category-select-with-create"
        classNames={{ popup: { root: 'category-dropdown-with-create' } }}
        style={{
          ...formStyles.inputStyle,
          borderColor: error ? token.colorError : undefined,
        }}
        status={error ? 'error' : ''}
      />
      {error && (
        <Text style={{ fontSize: token.fontSizeSM, color: token.colorError }}>{error}</Text>
      )}
    </>
  );
};

export default CategorySelectWithCreate;
